import React, {ReactNode, useCallback, useEffect} from 'react';
import {useDispatch} from 'react-redux';

import * as analytics from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {COLLABORATION_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import getRealtimeChannel from 'realtime/getRealtimeChannel';
import * as PresenceReduxActions from 'realtime/PresenceReduxActions';
import RealtimeChannel, {
  PresenceMessage,
  PresenceMessageType,
  RealtimeChannelCapability,
  RealtimeError,
} from 'realtime/RealtimeChannel';
import {getCollaborationAnalyticsMetadata} from 'realtime/util/analytics/CollaborationAnalyticsMetadata';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';
import * as logging from 'util/Logging';

export default function ResourcePresenceManager(props: {
  fileId: string;
  children?: ReactNode;
}): null | JSX.Element {
  const dispatch = useDispatch();

  const presenceEventHandler = useCallback(
    (msg: PresenceMessage): void => {
      switch (msg.type) {
        case PresenceMessageType.ENTER:
        case PresenceMessageType.PRESENT:
          // Whenever presence.subscribe() is called, PresenceMessageType.PRESENT messages are immediately
          // fired for all users currently present in the channel.
          dispatch(PresenceReduxActions.presenceEnter(props.fileId, msg.userId, msg.connectionId));
          break;
        case PresenceMessageType.LEAVE:
          dispatch(PresenceReduxActions.presenceLeave(props.fileId, msg.userId, msg.connectionId));
          break;
        default:
        // NOOP
      }
    },
    [props.fileId, dispatch]
  );

  useEffect(() => {
    let channel: RealtimeChannel | null = null;

    getRealtimeChannel(getRealtimeChannelNameForApiId(props.fileId), [
      RealtimeChannelCapability.SUBSCRIBE,
      RealtimeChannelCapability.PRESENCE,
    ])
      .then((ch: RealtimeChannel) => {
        channel = ch;
        channel.presence.enter((err: RealtimeError) => {
          assertAndContinue(err == null, 'error entering realtime channel', {err});
        });
        channel.presence.subscribe(presenceEventHandler);
        channel.presence.get((err, members) => {
          if (err) {
            logging.warn(`error retrieving realtime channel presence list`, {extra: {err}});
            return;
          }
          analytics.trackEvent(COLLABORATION_ANALYTICS_ACTIONS.ENTERED_PRESENCE, {
            ...getCollaborationAnalyticsMetadata(props.fileId),
            currentPresentConnections: members.length,
            currentPresentUsers: new Set(members.map((member) => member.userId)).size,
          });

          dispatch(
            PresenceReduxActions.presenceMultiEnter(
              props.fileId,
              members.map((member) => ({userId: member.userId, ablyConnectionId: member.connectionId}))
            )
          );
        });
      })
      .catch((err: Error) => {
        if (err instanceof RealtimeError) {
          logging.warn(`error connecting to realtime channel`, {extra: {err}});
          return;
        }

        throw err;
      });
    return () => {
      if (channel) {
        channel.presence.unsubscribe();
        channel.presence.leave();
        analytics.trackEvent(
          COLLABORATION_ANALYTICS_ACTIONS.EXITED_PRESENCE,
          getCollaborationAnalyticsMetadata(props.fileId)
        );
        channel.stop();
        dispatch(PresenceReduxActions.presenceCleanup(props.fileId));
      }
    };
  }, [props.fileId, dispatch, presenceEventHandler]);

  if (props.children != null) {
    return <>{props.children}</>;
  }

  return null;
}
